/**
 * Sync API
 *
 * Frontend client for the Jira/Azure DevOps story sync endpoints
 * (app/backend routes ported from the retired agent service — Tasks 10-12).
 */

import fetchWithAuth from "./secureFetch";

const BACKEND_URI = "";

export type SyncStatus = "NOT_READY" | "NOT_SYNCED" | "SYNCED" | "LOCAL_CHANGED" | "REMOTE_CHANGED" | "CONFLICT" | "ORPHANED";
export type SyncVendor = "JIRA" | "AZURE_DEVOPS";

export interface SyncScope {
    type: "story" | "feature" | "product";
    id: string | number;
}

export interface SyncStoryRow {
    story_id: string;
    title: string;
    subtitle: string;
    feature_id: number | null;
    feature_title: string | null;
    status: SyncStatus;
    external_key: string | null;
    external_url: string | null;
    last_synced_at: string | null;
    last_error: string | null;
}

export interface SyncScopeStatus {
    vendor: SyncVendor | null;
    linked: boolean;
    connected: boolean;
    remote_check_failed: boolean;
    counts: Record<string, number>;
    stories: SyncStoryRow[];
}

export interface SyncPushEvent {
    story_id: string;
    step: "preparing" | "pushing" | "done" | "error";
    status?: string;
    external_key?: string;
    external_url?: string;
    error?: string;
}

export interface SyncFieldDiff {
    base: unknown;
    local: unknown;
    remote: unknown;
}

export interface SyncDiff {
    story_id: string;
    external_key: string;
    external_url: string | null;
    remote_updated_at: string;
    fields: Record<string, SyncFieldDiff>;
}

/**
 * Reads the `error` field from a JSON error body when present.
 * Backend contract: 500s return a generic `{error: "Internal error"}`;
 * 404 on a foreign/unknown scope returns `{error: "Not found"}`.
 */
async function throwOnError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
        const body = await response.json();
        message = body.error || fallback;
    } catch {
        /* non-JSON error body */
    }
    throw new Error(message);
}

export async function getSyncStatus(
    scope: SyncScope,
    idToken: string,
    opts?: { ambient?: boolean }
): Promise<SyncScopeStatus> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/sync/status`, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts?.ambient ? { scope, ambient: true } : { scope })
    });
    if (!response.ok) await throwOnError(response, "Failed to load sync status");
    return response.json();
}

/** Exported for tests. Emits parsed `data:` frames; returns unconsumed remainder. */
export function parseSSEBuffer(buffer: string, onEvent: (e: SyncPushEvent) => void): string {
    const frames = buffer.split("\n\n");
    const rest = frames.pop() || "";
    for (const frame of frames) {
        const line = frame.trim();
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6);
        if (payload === "[DONE]") continue;
        try {
            onEvent(JSON.parse(payload));
        } catch {
            // skip malformed frame
        }
    }
    return rest;
}

export async function pushStories(
    storyIds: string[],
    idToken: string,
    onEvent: (e: SyncPushEvent) => void
): Promise<void> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/sync/push`, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story_ids: storyIds })
    });
    if (!response.ok || !response.body) await throwOnError(response, "Sync push failed");
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseSSEBuffer(buffer, onEvent);
    }
}

export async function getSyncDiff(storyId: string, idToken: string): Promise<SyncDiff> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/sync/diff/${storyId}`, idToken, { method: "GET" });
    if (!response.ok) await throwOnError(response, "Failed to load diff");
    return response.json();
}

export interface PullResult {
    status: string;
    story_id?: string;
    external_key?: string | null;
    /** Section + fresh HTML of the ticket content the pull wrote, so an open
     *  editor can refresh itself in place. Null when no ticket row changed. */
    section_id?: number | null;
    description_html?: string | null;
}

export async function pullStory(storyId: string, idToken: string): Promise<PullResult> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/sync/pull`, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story_id: storyId })
    });
    if (!response.ok) await throwOnError(response, "Failed to pull updates");
    return response.json();
}

/**
 * Resolve a sync conflict.
 *
 * API asymmetry closed here so the caller (Task 14 hook) stays simple:
 * - "theirs" pulls the remote copy; backend surfaces failures as HTTP errors
 *   (404/401/400), which `throwOnError` already turns into a rejection.
 * - "ours" pushes the local copy via the same code path as `pushStories`, so
 *   the backend always answers 200 with `{events: [...]}` — even on failure.
 *   A failure shows up as an event with `step: "error"` inside that 200
 *   response instead of a non-2xx status. We inspect the events here and
 *   throw using the first error we find, so callers can treat this function
 *   as a plain promise that rejects on any failure.
 *
 * For "theirs" the backend's single event is the pull result — returned so
 * callers can refresh an open editor with the pulled content.
 */
export async function resolveConflict(
    storyId: string,
    resolution: "ours" | "theirs",
    idToken: string
): Promise<PullResult | undefined> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/sync/resolve`, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story_id: storyId, resolution })
    });
    if (!response.ok) await throwOnError(response, "Failed to resolve conflict");

    const body = await response.json();
    if (resolution === "ours") {
        const events: Array<{ step?: string; error?: string }> = body?.events || [];
        const errorEvent = events.find((e) => e.step === "error");
        if (errorEvent) {
            throw new Error(errorEvent.error || "Failed to resolve conflict");
        }
        return undefined;
    }
    return body?.events?.[0] as PullResult | undefined;
}
